import React, { useEffect, lazy, Suspense } from 'react';
import { Routes, Route, Link, useLocation, Navigate } from 'react-router-dom';
import { trackPageView } from './lib/analytics';
import CircularGallery from './components/CircularGallery';
import Navbar from './components/Navbar';
import Login from './pages/Login';
import Signup from './pages/Signup';
import ResetPassword from './pages/ResetPassword';
import VerifyEmail from './pages/VerifyEmail';
import PrivacyPolicy from './pages/PrivacyPolicy';
import TermsOfService from './pages/TermsOfService';
import ReceiptPage from './pages/receipt/ReceiptPage';
import { AuthProvider } from './contexts/auth/AuthContext';
import { ServiceProvider } from './contexts/services/ServiceContext';
import { NotificationProvider } from './contexts/notifications/NotificationContext';
import { AskLotusProvider } from './contexts/askLotus/AskLotusContext';
import ProtectedRoute from './contexts/auth/ProtectedRoute';
import AdminRoute from './contexts/auth/AdminRoute';
import CrewRoute from './contexts/auth/CrewRoute';
import ScrollToTop from './components/ScrollToTop';
import AskLotusWidget from './components/askLotus/AskLotusWidget';
// Home is the default landing route — keep it eager so the browser doesn't have to
// fetch a second chunk before rendering "/". Lazy-loading the homepage adds a
// serialized network round-trip that hurts LCP/TTI on mobile-throttled Lighthouse.
import Home from './pages/Home';

// Lazy-loaded routes: each page becomes its own chunk, downloaded only when the
// user navigates there. Same Suspense fallback (line ~76) covers all of them.
const About = lazy(() => import('./pages/About'));
const Services = lazy(() => import('./pages/Services'));
const Gallery = lazy(() => import('./pages/Gallery'));
const Testimonials = lazy(() => import('./pages/Testimonials'));
const Contact = lazy(() => import('./pages/Contact'));
const Membership = lazy(() => import('./pages/Membership'));
const Promotion = lazy(() => import('./pages/Promotion'));
const Booking = lazy(() => import('./pages/Booking'));
const Blog = lazy(() => import('./pages/Blog'));
const BlogPost = lazy(() => import('./pages/BlogPost'));
const JoinPage = lazy(() => import('./pages/JoinPage'));
const JoinSuccessPage = lazy(() => import('./pages/JoinSuccessPage'));
const ProfilePage = lazy(() => import('./pages/customer/ProfilePage'));
const FacialConsentFormPage = lazy(() => import('./pages/public/FacialConsentFormPage'));
const AdminLayout = lazy(() => import('./components/admin/AdminLayout'));
const Dashboard = lazy(() => import('./pages/admin/Dashboard'));
const UserManagement = lazy(() => import('./pages/admin/UserManagement'));
const ContentManagement = lazy(() => import('./pages/admin/ContentManagement'));
const SchedulingSystem = lazy(() => import('./pages/admin/SchedulingSystem'));
const TeamManagement = lazy(() => import('./pages/admin/TeamManagement'));
const TimeReports = lazy(() => import('./pages/admin/TimeReports'));
const ServiceReports = lazy(() => import('./pages/admin/ServiceReports'));
const Messages = lazy(() => import('./pages/admin/Messages'));
const WalletManagement = lazy(() => import('./pages/admin/WalletManagement'));
const CrewLayout = lazy(() => import('./components/crew/CrewLayout'));
const CrewDashboard = lazy(() => import('./pages/crew/CrewDashboard'));
const CrewSchedule = lazy(() => import('./pages/crew/CrewSchedule'));
const CrewProfile = lazy(() => import('./pages/crew/CrewProfile'));

// Main App component with routes and layout
function App() {
  const location = useLocation();
  const isAdminOrCrew = location.pathname.includes('/admin') || location.pathname.includes('/crew');

  useEffect(() => {
    trackPageView(location.pathname);
  }, [location.pathname]);

  return (
    <AuthProvider>
      <ServiceProvider>
      <NotificationProvider>
      <AskLotusProvider>
      <ScrollToTop />
      <div className="min-h-screen bg-gradient-to-br from-[#F8F6F2] via-[#E8F5F2] to-[#F9E9E9] font-sans flex flex-col overflow-x-hidden">
        {/* Navigation Bar */}
        <Navbar />
        {/* Main content with top padding for fixed navbar */}
        <div className="pt-20 flex-1 flex flex-col">
          <main className="flex-1 flex flex-col">
          <Suspense fallback={<div className="flex-1 flex items-center justify-center"><div className="inline-block w-10 h-10 border-4 border-[#B2D8C5] border-t-[#232347] rounded-full animate-spin" /></div>}>
          <Routes>
            <Route path="/" element={<Home />} />
            <Route path="/about" element={<About />} />
            <Route path="/services" element={<Services />} />
            <Route path="/packages" element={<Navigate to="/services" replace />} />
            <Route path="/gallery" element={<Gallery />} />
            <Route path="/testimonials" element={<Testimonials />} />
            <Route path="/contact" element={<Contact />} />
            <Route path="/membership" element={<Membership />} />
            <Route path="/gift-card" element={<Navigate to="/membership" replace />} />
            <Route path="/promotion" element={<Promotion />} />
            <Route path="/blog" element={<Blog />} />
            <Route path="/blog/:slug" element={<BlogPost />} />
            <Route path="/booking" element={<Booking />} />

            {/* Membership Routes */}
            <Route path="/join" element={
              <ProtectedRoute>
                <JoinPage />
              </ProtectedRoute>
            } />
            <Route path="/join-success" element={
              <ProtectedRoute>
                <JoinSuccessPage />
              </ProtectedRoute>
            } />

            {/* Auth routes */}
            <Route path="/login" element={<Login />} />
            <Route path="/signup" element={<Signup />} />
            <Route path="/reset-password" element={<ResetPassword />} />
            <Route path="/verify-email" element={<VerifyEmail />} />

            {/* Legal pages */}
            <Route path="/privacy-policy" element={<PrivacyPolicy />} />
            <Route path="/terms-of-service" element={<TermsOfService />} />

            {/* Consent Form (public - accessible via token or logged in) */}
            <Route path="/consent-form" element={<FacialConsentFormPage />} />

            {/* Customer Profile */}
            <Route path="/profile" element={
              <ProtectedRoute>
                <ProfilePage />
              </ProtectedRoute>
            } />

            {/* Receipt Page - Public (receipt ID serves as access token) */}
            <Route path="/receipt/:id" element={<ReceiptPage />} />

            {/* Admin Routes */}
            <Route path="/admin" element={
              <AdminRoute>
                <AdminLayout>
                  <Dashboard />
                </AdminLayout>
              </AdminRoute>
            } />
            <Route path="/admin/dashboard" element={
              <AdminRoute>
                <AdminLayout>
                  <Dashboard />
                </AdminLayout>
              </AdminRoute>
            } />
            <Route path="/admin/users" element={
              <AdminRoute>
                <AdminLayout>
                  <UserManagement />
                </AdminLayout>
              </AdminRoute>
            } />
            <Route path="/admin/content" element={
              <AdminRoute>
                <AdminLayout>
                  <ContentManagement />
                </AdminLayout>
              </AdminRoute>
            } />
            <Route path="/admin/team" element={
              <AdminRoute>
                <AdminLayout>
                  <TeamManagement />
                </AdminLayout>
              </AdminRoute>
            } />
            <Route path="/admin/schedule" element={
              <AdminRoute>
                <AdminLayout>
                  <SchedulingSystem />
                </AdminLayout>
              </AdminRoute>
            } />
            <Route path="/admin/time-reports" element={
              <AdminRoute>
                <AdminLayout>
                  <TimeReports />
                </AdminLayout>
              </AdminRoute>
            } />
            <Route path="/admin/service-reports" element={
              <AdminRoute>
                <AdminLayout>
                  <ServiceReports />
                </AdminLayout>
              </AdminRoute>
            } />
            <Route path="/admin/messages" element={
              <AdminRoute>
                <AdminLayout>
                  <Messages />
                </AdminLayout>
              </AdminRoute>
            } />
            <Route path="/admin/wallet-management" element={
              <AdminRoute>
                <AdminLayout>
                  <WalletManagement />
                </AdminLayout>
              </AdminRoute>
            } />

            {/* Crew Routes */}
            <Route path="/crew" element={
              <CrewRoute>
                <CrewLayout>
                  <CrewDashboard />
                </CrewLayout>
              </CrewRoute>
            } />
            <Route path="/crew/dashboard" element={
              <CrewRoute>
                <CrewLayout>
                  <CrewDashboard />
                </CrewLayout>
              </CrewRoute>
            } />
            <Route path="/crew/schedule" element={
              <CrewRoute>
                <CrewLayout>
                  <CrewSchedule />
                </CrewLayout>
              </CrewRoute>
            } />
            <Route path="/crew/profile" element={
              <CrewRoute>
                <CrewLayout>
                  <CrewProfile />
                </CrewLayout>
              </CrewRoute>
            } />
            <Route path="/crew/messages" element={
              <CrewRoute>
                <CrewLayout>
                  <Messages />
                </CrewLayout>
              </CrewRoute>
            } />
            <Route path="/crew/users" element={
              <CrewRoute>
                <CrewLayout>
                  <UserManagement mode="crew" />
                </CrewLayout>
              </CrewRoute>
            } />
          </Routes>
          </Suspense>
          </main>
          {/* Ask Lotus Chat Widget */}
          <AskLotusWidget />
          {/* Footer - Only show on main site, not admin or crew */}
          {!isAdminOrCrew && (
            <footer className="w-full bg-gradient-to-b from-cream-100 to-brand-sage/30 text-brand-navy pt-20 pb-8 px-4 mt-auto">
              <div className="max-w-7xl mx-auto flex flex-col md:flex-row gap-12 md:gap-16 justify-between items-start md:items-stretch">
                {/* Left: Social & Brand */}
                <div className="flex-1 flex flex-col gap-6 mb-10 md:mb-0">
                  <div className="flex gap-4">
                    <a
                      href="https://www.instagram.com/healinglotuslian?igsh=ZG1uenJzaDI3anpz&utm_source=qr"
                      target="_blank"
                      rel="noopener noreferrer"
                      aria-label="Instagram"
                      className="text-brand-navy/75 hover:text-brand-teal transition"
                    >
                      <svg width="26" height="26" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
                        <path d="M12 2.2c3.2 0 3.584.012 4.85.07 1.17.053 1.805.25 2.227.415.56.217.96.477 1.38.896.42.42.68.82.896 1.38.165.422.362 1.057.415 2.227.058 1.266.07 1.65.07 4.85s-.012 3.584-.07 4.85c-.053 1.17-.25 1.805-.415 2.227-.217.56-.477.96-.896 1.38-.42.42-.82.68-1.38.896-.422.165-1.057.362-2.227.415-1.266.058-1.65.07-4.85.07s-3.584-.012-4.85-.07c-1.17-.053-1.805-.25-2.227-.415-.56-.217-.96-.477-1.38-.896-.42-.42-.68-.82-.896-1.38-.165-.422-.362-1.057-.415-2.227C2.212 15.584 2.2 15.2 2.2 12s.012-3.584.07-4.85c.053-1.17.25-1.805.415-2.227.217-.56.477-.96.896-1.38.42-.42.82-.68 1.38-.896.422-.165 1.057-.362 2.227-.415C8.416 2.212 8.8 2.2 12 2.2zm0 1.8c-3.146 0-3.507.012-4.747.068-1.018.046-1.57.217-1.938.36-.487.19-.834.414-1.2.78-.366.366-.59.713-.78 1.2-.143.368-.314.92-.36 1.938C3.012 8.493 3 8.854 3 12s.012 3.507.068 4.747c.046 1.018.217 1.57.36 1.938.19.487.414.834.78 1.2.366.366.713.59 1.2.78.368.143.92.314 1.938.36 1.24.056 1.6.068 4.747.068s3.507-.012 4.747-.068c1.018-.046 1.57-.217 1.938-.36.487-.19.834-.414 1.2-.78.366-.366.59-.713.78-1.2.143-.368.314-.92.36-1.938.056-1.24.068-1.6.068-4.747s-.012-3.507-.068-4.747c-.046-1.018-.217-1.57-.36-1.938-.19-.487-.414-.834-.78-1.2-.366-.366-.713-.59-1.2-.78-.368-.143-.92-.314-1.938-.36C15.507 4.012 15.146 4 12 4zm0 3.064A4.936 4.936 0 1 1 7.064 12 4.94 4.94 0 0 1 12 7.064zm0 8.136A3.2 3.2 0 1 0 8.8 12 3.2 3.2 0 0 0 12 15.2zm5.2-8.35a1.15 1.15 0 1 1-1.15-1.15 1.15 1.15 0 0 1 1.15 1.15z"/>
                      </svg>
                    </a>
                    <a
                      href="https://www.tiktok.com/@ladyfox125?_r=1&_t=ZP-95l6nFxm5xn"
                      target="_blank"
                      rel="noopener noreferrer"
                      aria-label="TikTok"
                      className="text-brand-navy/75 hover:text-brand-teal transition"
                    >
                      <svg width="26" height="26" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
                        <path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-5.2 1.74 2.89 2.89 0 0 1 2.31-4.64 2.93 2.93 0 0 1 .88.13V9.4a6.84 6.84 0 0 0-1-.05A6.33 6.33 0 0 0 5.6 20.1a6.34 6.34 0 0 0 10.86-4.43V8.31a8.16 8.16 0 0 0 4.77 1.53V6.42a4.85 4.85 0 0 1-1.64-.27z"/>
                      </svg>
                    </a>
                    <a
                      href="https://www.facebook.com/share/1G3Eq5Lvmw/?mibextid=wwXIfr"
                      target="_blank"
                      rel="noopener noreferrer"
                      aria-label="Facebook"
                      className="text-brand-navy/75 hover:text-brand-teal transition"
                    >
                      <svg width="26" height="26" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
                        <path d="M22 12a10 10 0 1 0-11.56 9.88v-6.99H7.9V12h2.54V9.8c0-2.5 1.49-3.89 3.77-3.89 1.09 0 2.24.2 2.24.2v2.47h-1.26c-1.24 0-1.63.77-1.63 1.56V12h2.77l-.44 2.89h-2.33v6.99A10 10 0 0 0 22 12z"/>
                      </svg>
                    </a>
                  </div>
                  <div className="text-xs tracking-[0.22em] text-brand-teal uppercase">Healing Lotus &middot; Dallas</div>
                  <div className="text-lg font-serif italic text-brand-navy/80 leading-relaxed max-w-sm">Relax, recharge, and renew &mdash; your wellness journey starts here.</div>
                </div>
                {/* Right: Link Groups */}
                <div className="flex-1 grid grid-cols-2 md:grid-cols-3 gap-8 md:gap-12">
                  <div>
                    <div className="uppercase text-xs font-semibold tracking-[0.22em] mb-4 text-brand-navy/55">Quick</div>
                    <ul className="space-y-2.5">
                      <li><Link to="/services" className="text-brand-navy/85 hover:text-brand-teal transition">Services</Link></li>
                      <li><Link to="/booking" className="text-brand-navy/85 hover:text-brand-teal transition">Book appointment</Link></li>
                      <li><Link to="/membership" className="text-brand-navy/85 hover:text-brand-teal transition">Membership</Link></li>
                    </ul>
                  </div>
                  <div>
                    <div className="uppercase text-xs font-semibold tracking-[0.22em] mb-4 text-brand-navy/55">About</div>
                    <ul className="space-y-2.5">
                      <li><Link to="/about" className="text-brand-navy/85 hover:text-brand-teal transition">About us</Link></li>
                      <li><Link to="/gallery" className="text-brand-navy/85 hover:text-brand-teal transition">Gallery</Link></li>
                      <li><Link to="/testimonials" className="text-brand-navy/85 hover:text-brand-teal transition">Testimonials</Link></li>
                      <li><Link to="/blog" className="text-brand-navy/85 hover:text-brand-teal transition">Journal</Link></li>
                      <li><Link to="/contact" className="text-brand-navy/85 hover:text-brand-teal transition">Contact</Link></li>
                    </ul>
                  </div>
                  <div>
                    <div className="uppercase text-xs font-semibold tracking-[0.22em] mb-4 text-brand-navy/55">Visit</div>
                    <a
                      href="https://maps.google.com/?q=4222+Trinity+Mills+Rd+Suite+120,+Dallas,+TX+75287"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="block text-brand-navy/85 hover:text-brand-teal transition leading-relaxed"
                    >
                      4222 Trinity Mills Rd<br />Suite 120<br />Dallas, TX 75287
                    </a>
                    <a href="tel:+19729980431" className="inline-block mt-4 text-sm text-brand-navy/85 hover:text-brand-teal transition tabular-nums">+1 (972) 998-0431</a>
                  </div>
                </div>
              </div>
              {/* Hairline divider above legal/copyright row */}
              <div className="max-w-7xl mx-auto mt-14 pt-6 border-t border-brand-sage/40 flex flex-col md:flex-row justify-between items-center gap-4 text-xs text-brand-navy/60">
                <div className="font-serif">&copy; 2026 Healing Lotus. All rights reserved.</div>
                <div className="flex gap-5">
                  <Link to="/privacy-policy" className="hover:text-brand-teal transition">Privacy policy</Link>
                  <Link to="/terms-of-service" className="hover:text-brand-teal transition">Terms of service</Link>
                </div>
              </div>
            </footer>
          )}
        </div>
      </div>
      </AskLotusProvider>
      </NotificationProvider>
      </ServiceProvider>
    </AuthProvider>
  );
}

export default App;
